import { useMemo, useState } from 'react'
import { View, Text } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Building2, BadgeCheck, Edit3, Navigation } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { DatePicker } from '@/components/ui/DatePicker'
import { TimeSlotPicker } from '@/components/ui/TimeSlotPicker'
import { Skeleton } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import {
  useTourHost,
  useTourHostPayouts,
  useSaveTourHostUnit,
  useTourHostDepartures,
  useCreateHostDeparture,
  useTourHostBookings,
} from '@/lib/hooks/use-tours'
import { toursPaymentErrorKey } from '@/lib/tours/payment-errors'
import { earliestTourBookingDateISO } from '@/lib/tours/booking-rules'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { formatCurrency } from '@/lib/utils/format'
import { formatTourDeparture } from '@/lib/tours/format'
import { toBookingDateTimeISO } from '@/lib/utils/dates'

const STATUS_KEY: Record<string, string> = {
  pending: 'tours.host.status_pending',
  documents_submitted: 'tours.host.status_documents',
  verified: 'tours.host.status_verified',
  suspended: 'tours.host.status_suspended',
}

export default function ToursHostScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { data, isLoading, error } = useTourHost()
  const operatorEnabled = Boolean(data?.host)
  const payouts = useTourHostPayouts(operatorEnabled)
  const saveUnit = useSaveTourHostUnit()
  const departures = useTourHostDepartures(operatorEnabled)
  const createDeparture = useCreateHostDeparture()
  const hostBookings = useTourHostBookings(operatorEnabled)
  const earliestBookableDate = useMemo(() => earliestTourBookingDateISO(), [])

  const [depExpId, setDepExpId] = useState<string | null>(null)
  const [depDate, setDepDate] = useState<string | null>(earliestBookableDate)
  const [depTime, setDepTime] = useState<string | null>(null)
  const [depSeats, setDepSeats] = useState('')
  const [depCargo, setDepCargo] = useState('')

  const [unitForm, setUnitForm] = useState<{
    id?: string
    label: string
    plate: string
    seats: string
    cargo: string
  }>({
    label: '',
    plate: '',
    seats: '',
    cargo: '',
  })
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const header = (
    <ScreenHeader
      title={t('tours.host.title')}
      backTestID="tours.host-back"
      onBackPress={() => router.back()}
    />
  )

  if (isLoading) {
    return (
      <ScreenContainer variant="dashboard">
        {header}
        <Text
          style={{
            marginTop: SPACING.lg,
            fontFamily: FONTS.body,
            fontSize: 15,
            color: colors.textSecondary,
          }}
        >
          {t('tours.loading')}
        </Text>
      </ScreenContainer>
    )
  }

  if (error || !data) {
    return (
      <ScreenContainer variant="dashboard">
        {header}
        <Text
          style={{
            marginTop: SPACING.lg,
            fontFamily: FONTS.body,
            fontSize: 14,
            color: colors.textError,
          }}
        >
          {t('tours.host.error')}
        </Text>
      </ScreenContainer>
    )
  }

  const submitUnit = async () => {
    setErrorKey(null)
    const seats = Number(unitForm.seats)
    const cargo = Number(unitForm.cargo)
    if (
      !unitForm.label.trim() ||
      !unitForm.plate.trim() ||
      !Number.isFinite(seats) ||
      seats < 1 ||
      !Number.isFinite(cargo) ||
      cargo <= 0
    ) {
      setErrorKey('tours.errors.bad_request')
      return
    }
    try {
      await saveUnit.mutateAsync({
        id: unitForm.id,
        label: unitForm.label.trim(),
        licensePlate: unitForm.plate.trim(),
        seats,
        cargoVolumeM3: cargo,
      })
      setUnitForm({ label: '', plate: '', seats: '', cargo: '' })
    } catch (err) {
      setErrorKey(toursPaymentErrorKey((err as { code?: string }).code))
    }
  }

  const submitDeparture = async () => {
    setErrorKey(null)
    const experienceId = depExpId ?? departures.data?.experiences[0]?.id ?? null
    const seats = Number(depSeats)
    const cargo = Number(depCargo)
    if (
      !experienceId ||
      !depDate ||
      !depTime ||
      !Number.isFinite(seats) ||
      seats < 1 ||
      !Number.isFinite(cargo) ||
      cargo <= 0
    ) {
      setErrorKey('tours.errors.bad_request')
      return
    }
    const startsAt = toBookingDateTimeISO(depDate, depTime)
    if (depDate < earliestBookableDate) {
      setErrorKey('tours.errors.minimum_lead_time')
      return
    }
    try {
      await createDeparture.mutateAsync({
        experienceId,
        startsAt,
        seatsCapacity: seats,
        cargoVolumeM3: cargo,
      })
      setDepDate(earliestBookableDate)
      setDepTime(null)
      setDepSeats('')
      setDepCargo('')
    } catch (err) {
      setErrorKey(toursPaymentErrorKey((err as { code?: string }).code))
    }
  }

  if (!data.host) {
    return (
      <ScreenContainer variant="dashboard">
        {header}
        <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
          <EmptyState
            illustration={<Building2 size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.host.operator_only_title')}
            description={t('tours.host.operator_only_body')}
          />
        </View>
      </ScreenContainer>
    )
  }

  const host = data.host
  const balance = payouts.data?.balance

  return (
    <ScreenContainer variant="dashboard" scroll={false}>
      <KeyboardAwareView includeTopInset={false} showsVerticalScrollIndicator={false}>
        {header}
        <View style={{ paddingHorizontal: SPACING.lg }}>
          <View style={{ marginTop: SPACING.md }}>
            <Card>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Building2 size={20} color={colors.textAccent} strokeWidth={1.8} />
                <Text
                  style={{
                    marginLeft: SPACING.sm,
                    flex: 1,
                    fontFamily: FONTS.bodyBold,
                    fontSize: 17,
                    color: colors.textPrimary,
                  }}
                >
                  {host.displayName}
                </Text>
                <BadgeCheck size={18} color={colors.success} strokeWidth={1.8} />
              </View>
              {host.businessName ? (
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    color: colors.textSecondary,
                  }}
                >
                  {host.businessName}
                </Text>
              ) : null}
              <View
                style={{
                  marginTop: SPACING.sm,
                  alignSelf: 'flex-start',
                  paddingHorizontal: SPACING.sm,
                  paddingVertical: 3,
                  borderRadius: RADIUS.full,
                  backgroundColor: colors.surfaceSubtle,
                }}
              >
                <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
                  {t(STATUS_KEY[host.verificationStatus] ?? 'tours.host.status_pending')}
                </Text>
              </View>
            </Card>
          </View>

          <View style={{ marginTop: SPACING.md }}>
            <Card>
              <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textMuted }}>
                {t('tours.host.available')}
              </Text>
              <Text
                style={{
                  marginTop: 2,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 26,
                  color: colors.textAccent,
                }}
                testID="tours.host-balance"
              >
                {formatCurrency(balance?.availableCents ?? 0)}
              </Text>
              <View style={{ flexDirection: 'row', marginTop: SPACING.sm, gap: SPACING.lg }}>
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                  {t('tours.host.earned')}: {formatCurrency(balance?.earnedCents ?? 0)}
                </Text>
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                  {t('tours.host.disbursed')}: {formatCurrency(balance?.disbursedCents ?? 0)}
                </Text>
              </View>
            </Card>
          </View>

          <Text
            style={{
              marginTop: SPACING.xl,
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('tours.host.units_title')}
          </Text>
          {data.units.length === 0 ? (
            <Text
              style={{
                marginTop: SPACING.sm,
                fontFamily: FONTS.body,
                fontSize: 14,
                color: colors.textMuted,
              }}
            >
              {t('tours.host.no_units')}
            </Text>
          ) : (
            <View style={{ marginTop: SPACING.sm, gap: SPACING.sm }}>
              {data.units.map((unit) => (
                <Pressable
                  key={unit.id}
                  onPress={() =>
                    setUnitForm({
                      id: unit.id,
                      label: unit.label,
                      plate: unit.licensePlate,
                      seats: String(unit.seats),
                      cargo: String(unit.cargoVolumeM3),
                    })
                  }
                  accessibilityRole="button"
                  accessibilityLabel={t('tours.host.edit_unit')}
                  testID={`tours.host-unit-${unit.id}`}
                >
                  <Card>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontFamily: FONTS.bodyBold,
                            fontSize: 15,
                            color: colors.textPrimary,
                          }}
                        >
                          {unit.label}
                        </Text>
                        <Text
                          style={{
                            marginTop: 2,
                            fontFamily: FONTS.body,
                            fontSize: 13,
                            color: colors.textSecondary,
                          }}
                        >
                          {unit.licensePlate} · {unit.seats} {t('tours.seats')} ·{' '}
                          {unit.cargoVolumeM3} m³
                        </Text>
                      </View>
                      <Edit3 size={18} color={colors.textMuted} strokeWidth={1.8} />
                    </View>
                  </Card>
                </Pressable>
              ))}
            </View>
          )}

          <View style={{ marginTop: SPACING.md }}>
            <Card>
              <View style={{ gap: SPACING.sm }}>
                <TextInput
                  label={t('tours.host.unit_label')}
                  value={unitForm.label}
                  onChangeText={(v) => setUnitForm((f) => ({ ...f, label: v }))}
                  testID="tours.host-unit-label"
                />
                <TextInput
                  label={t('tours.host.unit_plate')}
                  value={unitForm.plate}
                  onChangeText={(v) => setUnitForm((f) => ({ ...f, plate: v }))}
                />
                <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
                  <View style={{ flex: 1 }}>
                    <TextInput
                      label={t('tours.host.unit_seats')}
                      value={unitForm.seats}
                      onChangeText={(v) =>
                        setUnitForm((f) => ({ ...f, seats: v.replace(/\D/g, '') }))
                      }
                      keyboardType="number-pad"
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <TextInput
                      label={t('tours.host.unit_cargo')}
                      value={unitForm.cargo}
                      onChangeText={(v) =>
                        setUnitForm((f) => ({ ...f, cargo: v.replace(/[^\d.]/g, '') }))
                      }
                      keyboardType="decimal-pad"
                    />
                  </View>
                </View>
              </View>
            </Card>
          </View>

          {errorKey ? (
            <Text
              style={{
                marginTop: SPACING.md,
                fontFamily: FONTS.body,
                fontSize: 14,
                color: colors.textError,
              }}
            >
              {t(errorKey)}
            </Text>
          ) : null}

          <Button
            title={unitForm.id ? t('tours.host.save_changes') : t('tours.host.add_unit')}
            size="lg"
            loading={saveUnit.isPending}
            onPress={submitUnit}
            style={{ marginTop: SPACING.lg }}
            testID="tours.host-add-unit"
          />
          {unitForm.id ? (
            <Button
              title={t('tours.host.cancel')}
              variant="secondary"
              size="lg"
              onPress={() => setUnitForm({ label: '', plate: '', seats: '', cargo: '' })}
              style={{ marginTop: SPACING.sm }}
            />
          ) : null}

          <Text
            style={{
              marginTop: SPACING.xl,
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('tours.host.departures_title')}
          </Text>
          {departures.isLoading ? (
            <View style={{ marginTop: SPACING.sm }}>
              <Skeleton width="100%" height={48} borderRadius={RADIUS.lg} />
            </View>
          ) : (departures.data?.experiences.length ?? 0) === 0 ? (
            <Text
              style={{
                marginTop: SPACING.sm,
                fontFamily: FONTS.body,
                fontSize: 14,
                color: colors.textMuted,
              }}
            >
              {t('tours.host.no_experiences')}
            </Text>
          ) : (
            <View style={{ marginTop: SPACING.sm, gap: SPACING.md }}>
              {departures.data!.experiences.map((exp) => {
                const list = (departures.data?.departures ?? []).filter(
                  (d) => d.experienceId === exp.id,
                )
                return (
                  <View key={exp.id}>
                    <Text
                      style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}
                    >
                      {exp.title}
                    </Text>
                    {list.length === 0 ? (
                      <Text
                        style={{
                          marginTop: 2,
                          fontFamily: FONTS.body,
                          fontSize: 13,
                          color: colors.textMuted,
                        }}
                      >
                        {t('tours.host.no_departures')}
                      </Text>
                    ) : (
                      list.slice(0, 6).map((d) => (
                        <Text
                          key={d.id}
                          style={{
                            marginTop: 2,
                            fontFamily: FONTS.body,
                            fontSize: 13,
                            color: colors.textPrimary,
                          }}
                        >
                          {formatTourDeparture(d.startsAt, i18n.language)} · {d.seatsCapacity}{' '}
                          {t('tours.seats')}
                        </Text>
                      ))
                    )}
                  </View>
                )
              })}
            </View>
          )}

          {(departures.data?.experiences.length ?? 0) > 0 ? (
            <View style={{ marginTop: SPACING.md }}>
              <Card>
                <Text
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 15, color: colors.textPrimary }}
                >
                  {t('tours.host.new_departure')}
                </Text>
                {(departures.data?.experiences.length ?? 0) > 1 ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: SPACING.sm,
                      marginTop: SPACING.sm,
                    }}
                  >
                    {departures.data!.experiences.map((exp) => {
                      const selected = (depExpId ?? departures.data!.experiences[0]?.id) === exp.id
                      return (
                        <Pressable
                          key={exp.id}
                          onPress={() => setDepExpId(exp.id)}
                          accessibilityRole="button"
                          accessibilityState={{ selected }}
                          accessibilityLabel={exp.title}
                          style={{
                            paddingHorizontal: SPACING.md,
                            paddingVertical: SPACING.xs + 2,
                            borderRadius: RADIUS.full,
                            borderWidth: 1,
                            borderColor: selected ? colors.textAccent : colors.border,
                            backgroundColor: selected ? colors.surfaceSubtle : colors.surface,
                          }}
                        >
                          <Text
                            style={{
                              fontFamily: FONTS.body,
                              fontSize: 13,
                              color: selected ? colors.textPrimary : colors.textSecondary,
                            }}
                          >
                            {exp.title}
                          </Text>
                        </Pressable>
                      )
                    })}
                  </View>
                ) : null}
                <View style={{ marginTop: SPACING.sm, gap: SPACING.sm }}>
                  <DatePicker
                    value={depDate}
                    onChange={setDepDate}
                    label={t('tours.checkout.date')}
                    minDate={earliestBookableDate}
                  />
                  <TimeSlotPicker
                    value={depTime}
                    onChange={setDepTime}
                    intervalMinutes={60}
                    label={t('tours.host.departure_time')}
                  />
                  <Text
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 12,
                      color: colors.textMuted,
                    }}
                  >
                    {t('tours.minimum_lead_time_hint')}
                  </Text>
                  <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
                    <View style={{ flex: 1 }}>
                      <TextInput
                        label={t('tours.host.unit_seats')}
                        value={depSeats}
                        onChangeText={(v) => setDepSeats(v.replace(/\D/g, ''))}
                        keyboardType="number-pad"
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <TextInput
                        label={t('tours.host.unit_cargo')}
                        value={depCargo}
                        onChangeText={(v) => setDepCargo(v.replace(/[^\d.]/g, ''))}
                        keyboardType="decimal-pad"
                      />
                    </View>
                  </View>
                </View>
              </Card>
            </View>
          ) : null}

          {(departures.data?.experiences.length ?? 0) > 0 ? (
            <Button
              title={t('tours.host.add_departure')}
              size="lg"
              loading={createDeparture.isPending}
              onPress={submitDeparture}
              style={{ marginTop: SPACING.lg }}
              testID="tours.host-add-departure"
            />
          ) : null}

          <Text
            style={{
              marginTop: SPACING.xl,
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('tours.host.host_bookings_title')}
          </Text>
          {hostBookings.isLoading ? (
            <View style={{ marginTop: SPACING.sm }}>
              <Skeleton width="100%" height={72} borderRadius={RADIUS.lg} />
            </View>
          ) : (hostBookings.data?.length ?? 0) === 0 ? (
            <Text
              style={{
                marginTop: SPACING.sm,
                fontFamily: FONTS.body,
                fontSize: 14,
                color: colors.textMuted,
              }}
            >
              {t('tours.host.no_bookings')}
            </Text>
          ) : (
            <View style={{ marginTop: SPACING.sm, gap: SPACING.sm }}>
              {hostBookings.data!.map((booking) => (
                <Card key={booking.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                    <View style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 15,
                          color: colors.textPrimary,
                        }}
                      >
                        {booking.title ?? t('tours.bookings.unknown')}
                      </Text>
                      {booking.startsAt ? (
                        <Text
                          style={{
                            marginTop: 2,
                            fontFamily: FONTS.body,
                            fontSize: 13,
                            color: colors.textSecondary,
                          }}
                        >
                          {formatTourDeparture(booking.startsAt, i18n.language)}
                        </Text>
                      ) : null}
                    </View>
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 16,
                        color: colors.textPrimary,
                      }}
                    >
                      {formatCurrency(booking.totalCents)}
                    </Text>
                  </View>
                  <Text
                    style={{
                      marginTop: SPACING.xs,
                      fontFamily: FONTS.body,
                      fontSize: 13,
                      color: colors.textMuted,
                    }}
                  >
                    {booking.passengerCount} · {t('tours.seats')}
                  </Text>
                  {['paid', 'in_progress', 'completed'].includes(booking.status) ? (
                    <Button
                      title={t('tours.host.open_operation')}
                      size="sm"
                      icon={<Navigation size={17} />}
                      onPress={() => router.push(`/(tabs)/tours/trip/${booking.id}`)}
                      style={{ marginTop: SPACING.md }}
                      testID={`tours.host-operation-${booking.id}`}
                    />
                  ) : (
                    <Text
                      style={{
                        marginTop: SPACING.md,
                        fontFamily: FONTS.body,
                        fontSize: 13,
                        color: colors.success,
                      }}
                    >
                      {t('tours.booking_status.completed')}
                    </Text>
                  )}
                </Card>
              ))}
            </View>
          )}
        </View>
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
